import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, BookOpen, Image, FileText, BookMarked, Heart, Video } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const belajarIslamSubMenu = [
  { name: 'Flayer Dakwah', path: '/belajar-islam/infografis', icon: Image, desc: 'Flyer & infografis islami' },
  { name: 'Artikel', path: '/belajar-islam/artikel', icon: FileText, desc: 'Tulisan & kajian tulis' },
  { name: 'Panduan Ibadah', path: '/belajar-islam/panduan-ibadah', icon: BookMarked, desc: 'Shalat, Haji, Umroh & lainnya' },
  { name: 'Doa & Dzikir', path: '/belajar-islam/doa-dzikir', icon: Heart, desc: 'Doa harian & dzikir pagi petang' },
  { name: 'Kajian', path: '/belajar-islam/kajian', icon: Video, desc: 'Video kajian Thubaproject' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileBelajarOpen, setIsMobileBelajarOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const navLinks = [
    { name: 'Beranda', path: '/' },
    { name: 'Tentang', path: '/about' },
    { name: 'Ekosistem', path: '/ecosystem' },
    { name: 'Program', path: '/program' },
    { name: 'Media', path: '/media' },
  ];

  const isActive = (path) => location.pathname === path;
  const isBelajarActive = location.pathname.startsWith('/belajar-islam');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsMobileBelajarOpen(false);
  }, [location.pathname]);

  // Reusable Donation Dialog Content
  const DonationDialogContent = () => (
    <DialogContent className="glass-card border-gold/30 max-w-md overflow-y-auto max-h-[90vh]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-serif font-bold text-gold mb-4 text-center">Salurkan Donasi Terbaik Anda</DialogTitle>
      </DialogHeader>
      <div className="p-4 text-center space-y-6">
        {/* QR Code / Flyer Image */}
        <div className="rounded-xl overflow-hidden border border-gold/20 max-w-[180px] mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_clean-menu-design/artifacts/wik7abko_image.png"
            alt="QRIS Donasi"
            className="w-full h-auto"
          />
        </div>

        {/* Manual Transfer Details */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <p className="text-sm text-muted-foreground mb-1">Transfer Manual</p>
          <p className="text-sm text-gold font-medium mb-1">Bank Syariah Indonesia (BSI)</p>
          <div className="flex items-center justify-center gap-2 mb-1">
            <p className="text-2xl font-mono font-bold text-white tracking-wider select-all">7741114474</p>
          </div>
          <p className="text-xs text-muted-foreground">an. Yayasan Thuba Minal Jannah</p>
        </div>

        {/* Confirmation Button */}
        <a
          href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20saya%20sudah%20transfer%20donasi%20untuk%20Thuba%20Project"
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <Button className="bg-primary text-black hover:bg-[#F3E5AB] font-bold w-full rounded-full">
            Konfirmasi via WhatsApp
          </Button>
        </a>
      </div>
    </DialogContent>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-nav transition-all duration-300">
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_clean-menu-design/artifacts/iazr7n03_cropped-Green-removebg-preview.png"
            alt="Thuba Logo"
            className="h-16 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-all duration-300 hover:text-gold relative group ${isActive(link.path) ? 'text-gold' : 'text-muted-foreground'
                }`}
            >
              {link.name}
              <span className={`absolute -bottom-1 left-0 h-[2px] bg-gold transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
            </Link>
          ))}

          {/* Belajar Islam Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              onMouseEnter={() => setIsDropdownOpen(true)}
              className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-all duration-300 hover:text-gold relative group ${isBelajarActive ? 'text-gold' : 'text-muted-foreground'}`}
            >
              <BookOpen size={15} className="mr-0.5" />
              Belajar Islam
              <ChevronDown
                size={15}
                className={`transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
              />
              <span className={`absolute -bottom-1 left-0 h-[2px] bg-gold transition-all duration-300 ${isBelajarActive ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
            </button>

            {/* Dropdown Panel */}
            <div
              onMouseLeave={() => setIsDropdownOpen(false)}
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 transition-all duration-300 origin-top
                ${isDropdownOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}
            >
              {/* Arrow */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-[#0D2B29] border-l border-t border-gold/20"></div>
              <div className="bg-[#0D2B29] border border-gold/20 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header link ke halaman utama Belajar Islam */}
                <Link
                  to="/belajar-islam"
                  className="flex items-center gap-3 px-5 py-4 bg-gold/10 border-b border-gold/10 hover:bg-gold/20 transition-colors"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <div className="w-8 h-8 bg-gold/20 rounded-lg flex items-center justify-center">
                    <BookOpen size={16} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gold">Belajar Islam</p>
                    <p className="text-xs text-muted-foreground">Semua konten islami</p>
                  </div>
                </Link>

                {/* Sub-menu items */}
                <div className="p-2">
                  {belajarIslamSubMenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsDropdownOpen(false)}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group/item hover:bg-white/5
                          ${isActive(item.path) ? 'bg-white/10 text-gold' : 'text-muted-foreground'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                          ${isActive(item.path) ? 'bg-gold/20' : 'bg-white/5 group-hover/item:bg-gold/10'}`}>
                          <Icon size={15} className={`transition-colors ${isActive(item.path) ? 'text-gold' : 'text-muted-foreground group-hover/item:text-gold'}`} />
                        </div>
                        <div>
                          <p className={`text-sm font-medium transition-colors ${isActive(item.path) ? 'text-gold' : 'text-foreground group-hover/item:text-gold'}`}>
                            {item.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="default"
                className="bg-primary hover:bg-[#F3E5AB] text-black font-semibold rounded-full px-6 shadow-[0_0_15px_rgba(212,175,55,0.2)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] transition-all duration-300"
              >
                Donasi
              </Button>
            </DialogTrigger>
            <DonationDialogContent />
          </Dialog>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground hover:text-gold transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-[#081c1a] border-b border-white/10 p-6 shadow-2xl animate-accordion-down">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-serif font-medium ${isActive(link.path) ? 'text-gold' : 'text-foreground'}`}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile Belajar Islam Accordion */}
            <div className="border-t border-white/10 pt-4">
              <button
                onClick={() => setIsMobileBelajarOpen(!isMobileBelajarOpen)}
                className={`flex items-center justify-between w-full text-lg font-serif font-medium ${isBelajarActive ? 'text-gold' : 'text-foreground'}`}
              >
                <span className="flex items-center gap-2">
                  <BookOpen size={18} />
                  Belajar Islam
                </span>
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-300 ${isMobileBelajarOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isMobileBelajarOpen && (
                <div className="mt-3 ml-6 flex flex-col gap-2 animate-accordion-down">
                  <Link
                    to="/belajar-islam"
                    onClick={() => setIsOpen(false)}
                    className={`text-sm font-medium py-2 ${isActive('/belajar-islam') ? 'text-gold' : 'text-muted-foreground'}`}
                  >
                    ✦ Semua Konten
                  </Link>
                  {belajarIslamSubMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`text-sm font-medium py-2 ${isActive(item.path) ? 'text-gold' : 'text-muted-foreground'}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary text-black font-bold mt-4 rounded-full">
                  Donasi Sekarang
                </Button>
              </DialogTrigger>
              <DonationDialogContent />
            </Dialog>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
